import { Link } from '@ipfabric/ui-components/Link/Link';
import {
  type NodeSn,
  type TableColumnFormatLabels,
  brand,
  displayTokenToLabelFilter,
  getValueFromKeyOfType,
} from '@ipfabric/utils';
import { isReservedLabelName } from '@ipfabric/utils/labels';
import type { RenderedTableCell } from 'components/Table/cells/RenderedTableCell';
import {
  ARRAY_CELL_MAX_ITEMS,
  maybeWrapWithMore,
} from 'components/Table/cells/helpers/trimLargeArray';
import { readTableRowString } from 'components/Table/helpers/getLabelsExplorerSources';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/lib/function';
import type { ReactNode } from 'react';
import { unsafeWrapWithReactKeyByIndex } from 'utils/react/unsafeWrapWithReactKeyByIndex';
import type { RenderCellContentArguments } from '../model/TableCellContentRenderFunction';

export const renderLabelsCell = <TRow,>(
  format: TableColumnFormatLabels<TRow>,
  { row, context, severity }: RenderCellContentArguments<TRow>,
): RenderedTableCell => {
  const labels = getValueFromKeyOfType(row, format.field);

  // Reserved ipf_* labels are surfaced through dedicated columns (site / domain),
  // so they are hidden from the generic labels cell.
  const displayLabels = (labels ?? []).filter(
    (token) => !isReservedLabelName(displayTokenToLabelFilter(token).name),
  );

  if (displayLabels.length === 0) {
    return { content: null };
  }

  const labelsContent = pipe(
    displayLabels,
    (items) => items.slice(0, ARRAY_CELL_MAX_ITEMS),
    (items) => items.map(unsafeWrapWithReactKeyByIndex),
    array.reduce<ReactNode, ReactNode | null>(null, (prev, current) =>
      prev === null ? [current] : [prev, ', ', current],
    ),
    maybeWrapWithMore(displayLabels),
  );

  const selectedColumn = context.labelsColumns?.find(
    (column) => column.columnKey === String(format.field),
  );
  const selectedTargetId = selectedColumn
    ? readTableRowString(row, selectedColumn.targetIdField)
    : null;
  const anchor = context.deviceViewAnchor;
  const sn = anchor ? readTableRowString(row, anchor.snField) : null;
  const hostname = anchor
    ? readTableRowString(row, anchor.hostnameField)
    : null;
  const deviceSource =
    sn !== null && sn !== 'unknown' && hostname !== null
      ? {
          hostname,
          kind: 'device' as const,
          sn: brand<NodeSn>(sn),
        }
      : null;
  const canOpenExplorer =
    selectedColumn !== undefined &&
    selectedTargetId !== null &&
    !!context.onOpenExplorer &&
    !context.disableLinks;

  return {
    content: canOpenExplorer ? (
      <Link
        accent={severity === null ? 'positive' : 'contrast'}
        renderAs='button'
        onClick={() =>
          context.onOpenExplorer!(row, {
            selectedTabId: selectedColumn!.columnKey,
            sources: deviceSource === null ? [] : [deviceSource],
          })
        }
      >
        {labelsContent}
      </Link>
    ) : (
      labelsContent
    ),
  };
};
